import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Palette, Save, Upload, Check, RotateCcw, Eye, Image, Type, 
  Layout, Globe, FileText, Sparkles, X, ImagePlus, Video, Trash2, Code, Share2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBranding() {
  const { token, user } = useAuth();
  const { branding, templates, currentTemplate, saveBranding, previewTheme, resetPreview, loadMedia, reloadMedia, uploadMediaFile } = useTheme();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => { loadMedia(); }, [loadMedia]);
  const { t } = useLanguage();
  
  // Local state for editing
  const [localBranding, setLocalBranding] = useState(branding);
  const [activeTab, setActiveTab] = useState(isSuperAdmin ? 'templates' : 'branding');
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [videoUploadMode, setVideoUploadMode] = useState('url'); // 'url', 'file', 'embed'
  const [bgUploadMode, setBgUploadMode] = useState('url'); // 'url', 'file'
  
  // Partners state
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ name: '', logo_url: '', logo_base64: '', logo_size: 'medium', website_url: '', order: 0 });
  const [editingPartner, setEditingPartner] = useState(null);
  
  // File input refs
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const pwaIconInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const clientBgInputRef = useRef(null);

  // Fetch partners
  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPartners(data);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const handleAddPartner = async () => {
    if (!newPartner.name || (!newPartner.logo_url && !newPartner.logo_base64)) {
      toast.error('Nom et logo requis');
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newPartner)
      });
      if (res.ok) {
        toast.success('Partenaire ajouté');
        setNewPartner({ name: '', logo_url: '', logo_base64: '', logo_size: 'medium', website_url: '', order: 0 });
        fetchPartners();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleUpdatePartner = async (partnerId, data) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${partnerId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        toast.success('Partenaire mis à jour');
        setEditingPartner(null);
        fetchPartners();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${partnerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Partenaire supprimé');
        fetchPartners();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    setLocalBranding(branding);
  }, [branding]);

  // Track changes
  useEffect(() => {
    setHasChanges(JSON.stringify(localBranding) !== JSON.stringify(branding));
  }, [localBranding, branding]);

  const handleChange = (key, value) => {
    setLocalBranding(prev => ({ ...prev, [key]: value }));
  };

  const handleColorChange = (colorKey, value) => {
    setLocalBranding(prev => ({
      ...prev,
      customColors: { ...prev.customColors, [colorKey]: value }
    }));
  };

  // Handle file upload and convert to Base64
  const handleFileUpload = async (file, key, maxSize = 500) => {
    if (!file) return;
    
    if (file.size > maxSize * 1024) {
      toast.error('Fichier trop volumineux', { 
        description: `Taille max: ${maxSize}KB. Votre fichier: ${Math.round(file.size/1024)}KB` 
      });
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Type de fichier invalide', { description: 'Seules les images sont acceptées' });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      handleChange(key, fullUrl);
      toast.success('Image uploadee');
    } catch (err) {
      toast.error('Erreur upload', { description: err.response?.data?.detail || 'Echec' });
    }
  };

  const handleSelectTemplate = (templateId) => {
    setLocalBranding(prev => ({
      ...prev,
      templateId,
      customColors: {} // Reset custom colors when changing template
    }));
    previewTheme(templateId);
  };

  const handlePreview = () => {
    previewTheme(localBranding.templateId, localBranding.customColors, {
      backgroundImage: localBranding.backgroundImage,
      borderRadius: localBranding.borderRadius,
      fontHeading: localBranding.fontHeading,
      fontBody: localBranding.fontBody,
    });
    toast.info('Aperçu appliqué', { description: 'Les changements ne sont pas encore enregistrés' });
  };

  const handleReset = () => {
    setLocalBranding(branding);
    resetPreview();
    toast.info('Réinitialisé', { description: 'Retour aux paramètres enregistrés' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveBranding(localBranding, token);
      // Reload media from server to confirm save
      await reloadMedia();
      toast.success('Enregistré !', { description: 'Les paramètres de branding ont été mis à jour' });
      setHasChanges(false);
    } catch (error) {
      toast.error('Erreur', { description: 'Impossible d\'enregistrer les paramètres' });
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplate = templates[localBranding.templateId] || templates.coinbase;
  const effectiveColors = { ...selectedTemplate.colors, ...localBranding.customColors };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        {/* Header with actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading, Playfair Display, serif)' }}>
              Personnalisation
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Personnalisez l'apparence complète de votre plateforme
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <>
                <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
                <Button variant="outline" onClick={handlePreview} data-testid="preview-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Aperçu
                </Button>
              </>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving || !hasChanges}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="save-branding-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* First row of tabs */}
          <div className="space-y-2">
            <TabsList className={`grid ${isSuperAdmin ? 'grid-cols-6' : 'grid-cols-4'} w-full max-w-3xl bg-secondary/50`}>
              {isSuperAdmin && (
              <TabsTrigger value="templates" className="flex items-center gap-2" data-testid="tab-templates">
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Templates</span>
              </TabsTrigger>
              )}
              {isSuperAdmin && (
              <TabsTrigger value="colors" className="flex items-center gap-2" data-testid="tab-colors">
                <Palette className="w-4 h-4" />
                <span className="hidden sm:inline">Couleurs</span>
              </TabsTrigger>
              )}
              <TabsTrigger value="branding" className="flex items-center gap-2" data-testid="tab-branding">
                <Image className="w-4 h-4" />
                <span className="hidden sm:inline">Logo</span>
              </TabsTrigger>
              <TabsTrigger value="layout" className="flex items-center gap-2" data-testid="tab-layout">
                <Layout className="w-4 h-4" />
                <span className="hidden sm:inline">Mise en page</span>
              </TabsTrigger>
              <TabsTrigger value="legal" className="flex items-center gap-2" data-testid="tab-legal">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Legal</span>
              </TabsTrigger>
              <TabsTrigger value="sharing" className="flex items-center gap-2" data-testid="tab-sharing">
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Partage</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Second row - Dashboard Client */}
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Dashboard Client :</span>
              <TabsList className="bg-secondary/50">
                <TabsTrigger value="video" className="flex items-center gap-2" data-testid="tab-video">
                  <Video className="w-4 h-4" />
                  <span>Vidéo d'accueil</span>
                </TabsTrigger>
                <TabsTrigger value="background" className="flex items-center gap-2" data-testid="tab-background">
                  <ImagePlus className="w-4 h-4" />
                  <span>Image de fond</span>
                </TabsTrigger>
                <TabsTrigger value="partners" className="flex items-center gap-2" data-testid="tab-partners">
                  <Globe className="w-4 h-4" />
                  <span>Partenaires</span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Choisir un template
                </CardTitle>
                <CardDescription>
                  Sélectionnez un design de base inspiré des grandes plateformes bancaires et crypto
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.values(templates).map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleSelectTemplate(template.id)}
                      className={`group relative rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-[1.02] ${
                        localBranding.templateId === template.id 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      {/* Preview Card */}
                      <div 
                        className="aspect-[4/3] p-4"
                        style={{ backgroundColor: template.colors.background }}
                      >
                        {/* Mini dashboard preview */}
                        <div className="h-full flex flex-col gap-2">
                          {/* Header */}
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-6 h-6 rounded"
                              style={{ backgroundColor: template.colors.primary }}
                            />
                            <div 
                              className="h-2 w-16 rounded"
                              style={{ backgroundColor: template.colors.mutedForeground, opacity: 0.3 }}
                            />
                          </div>
                          {/* Cards */}
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div 
                              className="rounded p-2"
                              style={{ backgroundColor: template.colors.card }}
                            >
                              <div 
                                className="h-2 w-8 rounded mb-2"
                                style={{ backgroundColor: template.colors.mutedForeground, opacity: 0.3 }}
                              />
                              <div 
                                className="h-4 w-12 rounded"
                                style={{ backgroundColor: template.colors.primary }}
                              />
                            </div>
                            <div 
                              className="rounded p-2"
                              style={{ backgroundColor: template.colors.card }}
                            >
                              <div 
                                className="h-2 w-8 rounded mb-2"
                                style={{ backgroundColor: template.colors.mutedForeground, opacity: 0.3 }}
                              />
                              <div 
                                className="h-4 w-12 rounded"
                                style={{ backgroundColor: template.colors.accent }}
                              />
                            </div>
                          </div>
                          {/* Button */}
                          <div 
                            className="h-6 rounded flex items-center justify-center"
                            style={{ backgroundColor: template.colors.primary }}
                          >
                            <div 
                              className="h-2 w-12 rounded"
                              style={{ backgroundColor: template.colors.primaryForeground, opacity: 0.5 }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Info */}
                      <div className="p-3 bg-card border-t border-border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{template.name}</p>
                            <p className="text-xs text-muted-foreground">{template.description}</p>
                          </div>
                          {localBranding.templateId === template.id && (
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Personnaliser les couleurs
                </CardTitle>
                <CardDescription>
                  Modifiez les couleurs du template sélectionné ({selectedTemplate.name})
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Main Colors */}
                <div>
                  <h4 className="font-medium mb-4">Couleurs principales</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ColorPicker
                      label="Couleur primaire"
                      description="Boutons, liens, accents principaux"
                      value={effectiveColors.primary}
                      onChange={(v) => handleColorChange('primary', v)}
                      testId="color-primary"
                    />
                    <ColorPicker
                      label="Couleur d'accent"
                      description="Indicateurs de succès, badges"
                      value={effectiveColors.accent}
                      onChange={(v) => handleColorChange('accent', v)}
                      testId="color-accent"
                    />
                    <ColorPicker
                      label="Fond principal"
                      description="Arrière-plan de la page"
                      value={effectiveColors.background}
                      onChange={(v) => handleColorChange('background', v)}
                      testId="color-background"
                    />
                  </div>
                </div>

                {/* Secondary Colors */}
                <div>
                  <h4 className="font-medium mb-4">Couleurs secondaires</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ColorPicker
                      label="Fond des cartes"
                      description="Arrière-plan des cartes et modales"
                      value={effectiveColors.card}
                      onChange={(v) => handleColorChange('card', v)}
                      testId="color-card"
                    />
                    <ColorPicker
                      label="Texte principal"
                      description="Couleur du texte principal"
                      value={effectiveColors.foreground}
                      onChange={(v) => handleColorChange('foreground', v)}
                      testId="color-foreground"
                    />
                    <ColorPicker
                      label="Bordures"
                      description="Bordures des éléments"
                      value={effectiveColors.border}
                      onChange={(v) => handleColorChange('border', v)}
                      testId="color-border"
                    />
                  </div>
                </div>

                {/* Status Colors */}
                <div>
                  <h4 className="font-medium mb-4">Couleurs de statut</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <ColorPicker
                      label="Succès"
                      description="Validations, confirmations"
                      value={effectiveColors.success}
                      onChange={(v) => handleColorChange('success', v)}
                      testId="color-success"
                    />
                    <ColorPicker
                      label="Erreur"
                      description="Erreurs, alertes critiques"
                      value={effectiveColors.error}
                      onChange={(v) => handleColorChange('error', v)}
                      testId="color-error"
                    />
                    <ColorPicker
                      label="Avertissement"
                      description="Alertes, attention requise"
                      value={effectiveColors.warning}
                      onChange={(v) => handleColorChange('warning', v)}
                      testId="color-warning"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-4">Aperçu</h4>
                  <div 
                    className="rounded-lg p-6 border"
                    style={{ 
                      backgroundColor: effectiveColors.background,
                      borderColor: effectiveColors.border 
                    }}
                  >
                    <div className="flex flex-wrap gap-4">
                      <button
                        className="px-6 py-2 rounded font-semibold transition-all"
                        style={{ 
                          backgroundColor: effectiveColors.primary,
                          color: effectiveColors.primaryForeground,
                          borderRadius: localBranding.borderRadius
                        }}
                      >
                        Bouton principal
                      </button>
                      <button
                        className="px-6 py-2 rounded font-semibold transition-all border"
                        style={{ 
                          backgroundColor: effectiveColors.card,
                          color: effectiveColors.foreground,
                          borderColor: effectiveColors.border,
                          borderRadius: localBranding.borderRadius
                        }}
                      >
                        Bouton secondaire
                      </button>
                      <button
                        className="px-6 py-2 rounded font-semibold transition-all"
                        style={{ 
                          backgroundColor: effectiveColors.accent,
                          color: effectiveColors.accentForeground,
                          borderRadius: localBranding.borderRadius
                        }}
                      >
                        Accent
                      </button>
                    </div>
                    <div className="mt-4 flex gap-4">
                      <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: effectiveColors.success + '20', color: effectiveColors.success }}>
                        Succès
                      </span>
                      <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: effectiveColors.error + '20', color: effectiveColors.error }}>
                        Erreur
                      </span>
                      <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: effectiveColors.warning + '20', color: effectiveColors.warning }}>
                        Attention
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reset Colors Button */}
                {Object.keys(localBranding.customColors).length > 0 && (
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => handleChange('customColors', {})}
                      data-testid="reset-colors-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Réinitialiser les couleurs du template
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Logo et identité
                </CardTitle>
                <CardDescription>
                  Uploadez votre logo, favicon et image de fond
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Platform Name */}
                <div className="space-y-2">
                  <Label htmlFor="platformName">Nom de la plateforme</Label>
                  <Input
                    id="platformName"
                    value={localBranding.platformName}
                    onChange={(e) => handleChange('platformName', e.target.value)}
                    placeholder="Ma Banque Privée"
                    data-testid="platform-name-input"
                  />
                </div>

                {/* Show Platform Name Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <Label>Afficher le nom à côté du logo</Label>
                    <p className="text-sm text-muted-foreground">
                      Désactivez pour n'afficher que le logo
                    </p>
                  </div>
                  <Switch
                    checked={localBranding.showPlatformName}
                    onCheckedChange={(checked) => handleChange('showPlatformName', checked)}
                    data-testid="show-name-switch"
                  />
                </div>

                {/* Logo Upload */}
                <div className="space-y-4">
                  <Label>Logo</Label>
                  <div className="flex flex-col gap-4">
                    {/* Upload area */}
                    <div 
                      className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'logoUrl', 5000)}
                        data-testid="logo-upload-input"
                      />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Cliquez pour uploader un logo
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG, JPG, SVG • Max 500KB
                      </p>
                    </div>
                    
                    {/* Or URL input */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">ou URL</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <Input
                      value={localBranding.logoUrl?.startsWith('data:') ? '' : (localBranding.logoUrl || '')}
                      onChange={(e) => handleChange('logoUrl', e.target.value)}
                      placeholder="https://exemple.com/logo.png"
                      data-testid="logo-url-input"
                    />
                    
                    {/* Logo Preview & Size */}
                    {localBranding.logoUrl && (
                      <div className="p-4 bg-secondary/30 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Aperçu du logo</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChange('logoUrl', null)}
                            data-testid="remove-logo-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-center p-4 bg-card rounded border min-h-[80px]">
                          <img 
                            src={localBranding.logoUrl} 
                            alt="Logo preview" 
                            style={{ height: `${localBranding.logoSize || 40}px`, width: 'auto' }}
                            className="object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        
                        {/* Logo Size Slider */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Taille du logo</Label>
                            <span className="text-sm text-muted-foreground">{localBranding.logoSize || 40}px</span>
                          </div>
                          <Slider
                            value={[localBranding.logoSize || 40]}
                            onValueChange={(value) => handleChange('logoSize', value[0])}
                            min={24}
                            max={80}
                            step={4}
                            className="w-full"
                            data-testid="logo-size-slider"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Petit (24px)</span>
                            <span>Grand (80px)</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Favicon Upload */}
                <div className="space-y-4">
                  <Label>Favicon (icône onglet navigateur)</Label>
                  <div className="flex gap-4 items-start">
                    {/* Upload button */}
                    <div 
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors flex-shrink-0"
                      onClick={() => faviconInputRef.current?.click()}
                    >
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'faviconUrl', 2000)}
                        data-testid="favicon-upload-input"
                      />
                      <ImagePlus className="w-6 h-6 mx-auto text-muted-foreground" />
                      <p className="text-xs text-muted-foreground mt-1">Upload</p>
                    </div>
                    
                    {/* URL input */}
                    <div className="flex-1 space-y-2">
                      <Input
                        value={localBranding.faviconUrl || ''}
                        onChange={(e) => handleChange('faviconUrl', e.target.value)}
                        placeholder="https://exemple.com/favicon.ico"
                        data-testid="favicon-url-input"
                      />
                      <p className="text-xs text-muted-foreground">ICO ou PNG, 32x32px</p>
                    </div>
                    
                    {/* Preview */}
                    {localBranding.faviconUrl && (
                      <div className="flex items-center gap-2 p-2 bg-secondary/30 rounded">
                        <img 
                          src={localBranding.faviconUrl} 
                          alt="Favicon" 
                          className="w-8 h-8 object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleChange('faviconUrl', null)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* PWA App Icon Upload */}
                <div className="space-y-4 p-4 rounded-lg border border-primary/30 bg-primary/5">
                  <div>
                    <Label className="text-base font-semibold">Logo Application Mobile (PWA)</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ce logo s'affiche quand l'application est installée sur un téléphone (icône sur l'écran d'accueil)
                    </p>
                  </div>
                  <input
                    type="file"
                    ref={pwaIconInputRef}
                    className="hidden"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error('Fichier trop volumineux', { description: 'Taille max: 2MB' });
                        return;
                      }
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/upload-pwa-icon`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        if (!res.ok) throw new Error('Upload failed');
                        const data = await res.json();
                        handleChange('pwaIcon', data.url);
                        toast.success('Logo application importé et converti en PNG !');
                      } catch (err) {
                        toast.error("Erreur lors de l'import");
                      }
                    }}
                  />
                  <div className="flex items-center gap-4">
                    {localBranding.pwaIcon ? (
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-primary/30 bg-black flex items-center justify-center">
                          <img 
                            src={localBranding.pwaIcon.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${localBranding.pwaIcon}` : localBranding.pwaIcon}
                            alt="App icon" 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => pwaIconInputRef.current?.click()}>
                            Remplacer
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleChange('pwaIcon', null)}>
                            <X className="w-4 h-4 mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors flex-1"
                        onClick={() => pwaIconInputRef.current?.click()}
                      >
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Importer le logo de l'application mobile</p>
                        <p className="text-xs text-muted-foreground mt-1">PNG recommandé • 512x512px • Max 2MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Background Image Upload */}
                <div className="space-y-4">
                  <Label>Image de fond (page de connexion)</Label>
                  <div 
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => backgroundInputRef.current?.click()}
                  >
                    <input
                      ref={backgroundInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'backgroundImage', 2000)}
                      data-testid="background-upload-input"
                    />
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Cliquez pour uploader une image de fond
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      JPG, PNG • Max 2MB • Recommandé: 1920x1080
                    </p>
                  </div>
                  
                  {/* Or URL input */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">ou URL</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <Input
                    value={localBranding.backgroundImage?.startsWith('data:') ? '' : (localBranding.backgroundImage || '')}
                    onChange={(e) => handleChange('backgroundImage', e.target.value)}
                    placeholder="https://exemple.com/background.jpg"
                    data-testid="background-image-input"
                  />
                  
                  {/* Preview */}
                  {localBranding.backgroundImage && (
                    <div className="relative rounded-lg overflow-hidden aspect-video max-w-md">
                      <img 
                        src={localBranding.backgroundImage} 
                        alt="Background preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-sm">Aperçu de l'image de fond</span>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => handleChange('backgroundImage', null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Layout Tab */}
          <TabsContent value="layout" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layout className="w-5 h-5" />
                  Mise en page et typographie
                </CardTitle>
                <CardDescription>
                  Configurez les arrondis, les polices et les préférences d'affichage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Border Radius */}
                <div className="space-y-4">
                  <Label>Arrondis des éléments</Label>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { value: '0px', label: 'Carrés', preview: 'rounded-none' },
                      { value: '4px', label: 'Subtils', preview: 'rounded-sm' },
                      { value: '8px', label: 'Moyens', preview: 'rounded-md' },
                      { value: '16px', label: 'Arrondis', preview: 'rounded-xl' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleChange('borderRadius', option.value)}
                        className={`p-4 border-2 transition-all ${
                          localBranding.borderRadius === option.value
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        style={{ borderRadius: option.value }}
                        data-testid={`radius-${option.value}`}
                      >
                        <div 
                          className="w-full h-12 bg-primary mb-2"
                          style={{ borderRadius: option.value }}
                        />
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fonts */}
                <div className="space-y-4">
                  <Label>Police des titres</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { value: "'Playfair Display', serif", label: 'Élégant', sample: 'Playfair' },
                      { value: "'Inter', sans-serif", label: 'Moderne', sample: 'Inter' },
                      { value: "'Manrope', sans-serif", label: 'Épuré', sample: 'Manrope' },
                      { value: "'Source Sans Pro', sans-serif", label: 'Classique', sample: 'Source' },
                    ].map((font) => (
                      <button
                        key={font.value}
                        onClick={() => handleChange('fontHeading', font.value)}
                        className={`p-4 border-2 transition-all rounded-lg ${
                          localBranding.fontHeading === font.value
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`font-${font.label.toLowerCase()}`}
                      >
                        <span 
                          className="text-2xl font-bold block mb-1"
                          style={{ fontFamily: font.value }}
                        >
                          Aa
                        </span>
                        <span className="text-sm text-muted-foreground">{font.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Language */}
                <div className="space-y-4">
                  <Label>Langue par défaut</Label>
                  <div className="flex gap-4">
                    <Button
                      variant={localBranding.defaultLanguage === 'fr' ? 'default' : 'outline'}
                      onClick={() => handleChange('defaultLanguage', 'fr')}
                      className={localBranding.defaultLanguage === 'fr' ? 'bg-primary text-primary-foreground' : ''}
                      data-testid="lang-fr-btn"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Français
                    </Button>
                    <Button
                      variant={localBranding.defaultLanguage === 'en' ? 'default' : 'outline'}
                      onClick={() => handleChange('defaultLanguage', 'en')}
                      className={localBranding.defaultLanguage === 'en' ? 'bg-primary text-primary-foreground' : ''}
                      data-testid="lang-en-btn"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      English
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Tab */}
          <TabsContent value="legal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Mentions légales
                </CardTitle>
                <CardDescription>
                  Textes réglementaires et juridiques de votre plateforme
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="legalNotice">Régulation</Label>
                  <p className="text-xs text-muted-foreground">Ce texte s'affiche en bas de page côté client</p>
                  <Textarea
                    id="legalNotice"
                    value={localBranding.legalNotice || ''}
                    onChange={(e) => handleChange('legalNotice', e.target.value)}
                    rows={4}
                    placeholder="Raison sociale, siège social, numéro d'immatriculation, régulateur..."
                    data-testid="legal-notice-textarea"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="privacyPolicy">Politique de confidentialité et conditions générales d'utilisation</Label>
                  <p className="text-xs text-muted-foreground">Accessible via un lien cliquable en bas de page côté client. Si vide, le lien ne s'affiche pas.</p>
                  <Textarea
                    id="privacyPolicy"
                    value={localBranding.privacyPolicy || ''}
                    onChange={(e) => handleChange('privacyPolicy', e.target.value)}
                    rows={8}
                    placeholder="Article 1 — Collecte des données&#10;&#10;Article 2 — Utilisation&#10;&#10;Article 3 — Conditions générales..."
                    data-testid="privacy-policy-textarea"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Video Tab */}
          <TabsContent value="video" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Vidéo d'accueil
                </CardTitle>
                <CardDescription>
                  Ajoutez une vidéo de présentation sur le tableau de bord client. 
                  La vidéo s'affichera en haut du dashboard après connexion.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                  <div>
                    <Label className="text-base">Activer la vidéo d'accueil</Label>
                    <p className="text-sm text-muted-foreground">
                      Affiche une vidéo sur le tableau de bord des clients
                    </p>
                  </div>
                  <Switch
                    checked={localBranding.welcomeVideoEnabled || false}
                    onCheckedChange={(checked) => handleChange('welcomeVideoEnabled', checked)}
                    data-testid="video-enabled-switch"
                  />
                </div>

                {/* Video Input Method Selection */}
                <div className="space-y-3">
                  <Label>Méthode d'ajout de la vidéo</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Button 
                      variant={videoUploadMode === 'url' ? 'default' : 'outline'}
                      onClick={() => setVideoUploadMode('url')}
                      className="flex items-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      URL (YouTube/Vimeo)
                    </Button>
                    <Button 
                      variant={videoUploadMode === 'file' ? 'default' : 'outline'}
                      onClick={() => setVideoUploadMode('file')}
                      className="flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Importer un fichier
                    </Button>
                    <Button 
                      variant={videoUploadMode === 'embed' ? 'default' : 'outline'}
                      onClick={() => setVideoUploadMode('embed')}
                      className="flex items-center gap-2"
                    >
                      <Code className="w-4 h-4" />
                      Code d'intégration
                    </Button>
                  </div>
                </div>

                {/* URL Mode */}
                {videoUploadMode === 'url' && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <div className="space-y-2">
                      <Label>Lien YouTube ou Vimeo</Label>
                      <Input
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={localBranding.welcomeVideoUrl || ''}
                        onChange={(e) => handleChange('welcomeVideoUrl', e.target.value)}
                        data-testid="video-url-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Collez l'URL complète de votre vidéo YouTube ou Vimeo
                      </p>
                    </div>
                  </div>
                )}

                {/* File Upload Mode */}
                {videoUploadMode === 'file' && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <input
                      type="file"
                      ref={videoInputRef}
                      className="hidden"
                      accept="video/mp4,video/webm,video/ogg"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 150 * 1024 * 1024) {
                          toast.error('Fichier trop volumineux', { description: 'Taille max: 150MB' });
                          return;
                        }
                        setUploading(true);
                        setUploadProgress(0);
                        try {
                          const url = await uploadMediaFile(file, token, (pct) => setUploadProgress(pct));
                          handleChange('welcomeVideoFile', url);
                          handleChange('welcomeVideoFileName', file.name);
                          toast.success('Vidéo importée avec succès !');
                        } catch (err) {
                          console.error('Upload failed:', err);
                          toast.error("Erreur lors de l'import", { description: err?.response?.data?.detail || 'Veuillez réessayer' });
                        } finally {
                          setUploading(false);
                          setUploadProgress(0);
                        }
                      }}
                    />
                    <div className="flex flex-col items-center gap-4 p-8 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors">
                      {uploading ? (
                        <div className="text-center w-full">
                          <Video className="w-12 h-12 mx-auto mb-3 text-primary animate-pulse" />
                          <p className="font-medium mb-2">Import en cours... {uploadProgress}%</p>
                          <div className="w-full max-w-xs mx-auto h-2 bg-secondary rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary transition-all duration-300 rounded-full" 
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">Veuillez patienter...</p>
                        </div>
                      ) : localBranding.welcomeVideoFile ? (
                        <div className="text-center">
                          <Video className="w-12 h-12 mx-auto mb-2 text-primary" />
                          <p className="font-medium">{localBranding.welcomeVideoFileName || 'Vidéo importée'}</p>
                          {localBranding.welcomeVideoFile.startsWith('/api/') && (
                            <video 
                              src={`${process.env.REACT_APP_BACKEND_URL}${localBranding.welcomeVideoFile}`}
                              className="mt-3 rounded max-h-40 mx-auto"
                              controls
                              muted
                              preload="metadata"
                            />
                          )}
                          <div className="flex gap-2 mt-4">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => videoInputRef.current?.click()}
                            >
                              Remplacer
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => {
                                handleChange('welcomeVideoFile', null);
                                handleChange('welcomeVideoFileName', null);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-muted-foreground" />
                          <div className="text-center">
                            <p className="font-medium">Glissez votre vidéo ici ou cliquez pour importer</p>
                            <p className="text-sm text-muted-foreground">MP4, WebM, OGG - Max 100MB</p>
                          </div>
                          <Button onClick={() => videoInputRef.current?.click()}>
                            <Upload className="w-4 h-4 mr-2" />
                            Choisir un fichier
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Embed Code Mode */}
                {videoUploadMode === 'embed' && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <div className="space-y-2">
                      <Label>Code d'intégration YouTube</Label>
                      <Textarea
                        placeholder='<iframe width="560" height="315" src="https://www.youtube.com/embed/..." ...'
                        value={localBranding.welcomeVideoEmbed || ''}
                        onChange={(e) => handleChange('welcomeVideoEmbed', e.target.value)}
                        rows={4}
                        className="font-mono text-sm"
                        data-testid="video-embed-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Copiez le code d'intégration depuis YouTube : Partager → Intégrer → Copier le code
                      </p>
                      <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <p className="text-xs text-amber-500">
                          ⚠️ Note : Les vidéos YouTube affichent leur propre interface (titre, logo). 
                          Pour un résultat plus épuré sans branding externe, utilisez l'option "Importer un fichier".
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Titre de la vidéo (optionnel)</Label>
                    <Input
                      placeholder="Bienvenue sur notre plateforme"
                      value={localBranding.welcomeVideoTitle || ''}
                      onChange={(e) => handleChange('welcomeVideoTitle', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Position sur le dashboard</Label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      value={localBranding.welcomeVideoPosition || 'top'}
                      onChange={(e) => handleChange('welcomeVideoPosition', e.target.value)}
                    >
                      <option value="top">En haut (après les stats)</option>
                      <option value="bottom">En bas (avant les documents)</option>
                    </select>
                  </div>
                </div>

                {/* Autoplay & Loop & Height Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                    <div>
                      <Label>Lecture automatique</Label>
                      <p className="text-xs text-muted-foreground">Démarre automatiquement (muet)</p>
                    </div>
                    <Switch
                      checked={localBranding.welcomeVideoAutoplay || false}
                      onCheckedChange={(checked) => handleChange('welcomeVideoAutoplay', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                    <div>
                      <Label>Lecture en boucle</Label>
                      <p className="text-xs text-muted-foreground">Redémarre à la fin</p>
                    </div>
                    <Switch
                      checked={localBranding.welcomeVideoLoop || false}
                      onCheckedChange={(checked) => handleChange('welcomeVideoLoop', checked)}
                      data-testid="video-loop-switch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hauteur de la vidéo</Label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      value={localBranding.welcomeVideoHeight || 'medium'}
                      onChange={(e) => handleChange('welcomeVideoHeight', e.target.value)}
                    >
                      <option value="small">Petite (150px)</option>
                      <option value="medium">Moyenne (250px)</option>
                      <option value="large">Grande (350px)</option>
                      <option value="auto">Auto (16:9)</option>
                    </select>
                  </div>
                </div>

                {/* Crop Settings */}
                <div className="space-y-4 p-4 rounded-lg border border-border">
                  <Label className="text-base">Recadrage de la vidéo</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Ajustez pour rogner le haut et le bas de la vidéo (utile pour masquer les barres YouTube)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Rogner le haut ({localBranding.welcomeVideoCropTop || 0}%)</Label>
                      <Slider
                        value={[localBranding.welcomeVideoCropTop || 0]}
                        onValueChange={([val]) => handleChange('welcomeVideoCropTop', val)}
                        min={0}
                        max={30}
                        step={1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Rogner le bas ({localBranding.welcomeVideoCropBottom || 0}%)</Label>
                      <Slider
                        value={[localBranding.welcomeVideoCropBottom || 0]}
                        onValueChange={([val]) => handleChange('welcomeVideoCropBottom', val)}
                        min={0}
                        max={30}
                        step={1}
                      />
                    </div>
                  </div>
                </div>

                {/* Visual Position Picker */}
                {(localBranding.welcomeVideoFile || localBranding.welcomeVideoUrl || localBranding.welcomeVideoEmbed) && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Positionnement de la vidéo</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Cliquez et glissez sur l'aperçu pour positionner la vidéo dans le cadre
                        </p>
                      </div>
                      <button
                        className="text-xs px-3 py-1.5 rounded-md border border-input hover:bg-secondary/50 transition-colors"
                        onClick={() => handleChange('welcomeVideoObjectPosition', '50% 50%')}
                      >
                        Recentrer
                      </button>
                    </div>
                    <div
                      className="relative rounded-lg overflow-hidden cursor-crosshair select-none border border-border"
                      style={{ height: { small: '150px', medium: '250px', large: '350px', auto: '250px' }[localBranding.welcomeVideoHeight || 'medium'] || '250px' }}
                      onMouseDown={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const update = (ev) => {
                          const x = Math.max(0, Math.min(100, ((ev.clientX - rect.left) / rect.width) * 100));
                          const y = Math.max(0, Math.min(100, ((ev.clientY - rect.top) / rect.height) * 100));
                          handleChange('welcomeVideoObjectPosition', `${Math.round(x)}% ${Math.round(y)}%`);
                        };
                        update(e);
                        const onMove = (ev) => { ev.preventDefault(); update(ev); };
                        const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                        document.addEventListener('mousemove', onMove);
                        document.addEventListener('mouseup', onUp);
                      }}
                    >
                      {localBranding.welcomeVideoFile ? (
                        <video
                          src={localBranding.welcomeVideoFile.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${localBranding.welcomeVideoFile}` : localBranding.welcomeVideoFile}
                          className="w-full h-full pointer-events-none"
                          style={{ objectFit: 'cover', objectPosition: localBranding.welcomeVideoObjectPosition || '50% 50%' }}
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <div className="w-full h-full bg-black/50 flex items-center justify-center">
                          <p className="text-muted-foreground text-sm">Aperçu vidéo (positionnement disponible pour les fichiers uploadés)</p>
                        </div>
                      )}
                      {/* Crosshair indicator */}
                      {(() => {
                        const pos = (localBranding.welcomeVideoObjectPosition || '50% 50%').split(' ');
                        const px = parseFloat(pos[0]) || 50;
                        const py = parseFloat(pos[1]) || 50;
                        return (
                          <div
                            className="absolute pointer-events-none"
                            style={{ left: `${px}%`, top: `${py}%`, transform: 'translate(-50%, -50%)' }}
                          >
                            <div className="w-8 h-8 rounded-full border-2 border-white shadow-lg" style={{ background: 'rgba(255,255,255,0.15)' }}>
                              <div className="absolute top-1/2 left-1/2 w-2 h-2 -ml-1 -mt-1 rounded-full bg-white shadow" />
                            </div>
                            <div className="absolute top-1/2 left-0 w-full h-px bg-white/40" style={{ transform: 'translateY(-50%)' }} />
                            <div className="absolute left-1/2 top-0 h-full w-px bg-white/40" style={{ transform: 'translateX(-50%)' }} />
                          </div>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Position : {localBranding.welcomeVideoObjectPosition || '50% 50%'}
                    </p>
                  </div>
                )}

                {/* Preview */}
                {(localBranding.welcomeVideoUrl || localBranding.welcomeVideoEmbed) && (
                  <div className="space-y-2">
                    <Label>Aperçu</Label>
                    <div className="aspect-video rounded-lg overflow-hidden bg-black/50">
                      {localBranding.welcomeVideoUrl?.includes('youtube') ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${localBranding.welcomeVideoUrl.split('v=')[1]?.split('&')[0] || ''}?modestbranding=1&rel=0&showinfo=0`}
                          className="w-full h-full"
                          allowFullScreen
                          title="Aperçu vidéo"
                          frameBorder="0"
                        />
                      ) : localBranding.welcomeVideoEmbed ? (
                        <div 
                          className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
                          dangerouslySetInnerHTML={{ 
                            __html: localBranding.welcomeVideoEmbed
                              .replace(/width="[^"]*"/g, 'width="100%"')
                              .replace(/height="[^"]*"/g, 'height="100%"')
                          }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-muted-foreground">Aperçu non disponible</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Background Tab */}
          <TabsContent value="background" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Image de fond client
                </CardTitle>
                <CardDescription>
                  Ajoutez une image de fond qui s'affichera derrière tous les éléments du tableau de bord client
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                  <div>
                    <Label className="text-base">Activer l'image de fond</Label>
                    <p className="text-sm text-muted-foreground">
                      Affiche une image en arrière-plan du dashboard client
                    </p>
                  </div>
                  <Switch
                    checked={localBranding.clientBackgroundEnabled || false}
                    onCheckedChange={(checked) => handleChange('clientBackgroundEnabled', checked)}
                    data-testid="bg-enabled-switch"
                  />
                </div>

                {/* Upload Method Selection */}
                <div className="space-y-3">
                  <Label>Méthode d'ajout</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant={bgUploadMode === 'url' ? 'default' : 'outline'}
                      onClick={() => setBgUploadMode('url')}
                      className="flex items-center gap-2"
                    >
                      <Globe className="w-4 h-4" />
                      URL de l'image
                    </Button>
                    <Button 
                      variant={bgUploadMode === 'file' ? 'default' : 'outline'}
                      onClick={() => setBgUploadMode('file')}
                      className="flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Importer un fichier
                    </Button>
                  </div>
                </div>

                {/* URL Mode */}
                {bgUploadMode === 'url' && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <div className="space-y-2">
                      <Label>Lien de l'image</Label>
                      <Input
                        placeholder="https://exemple.com/image.jpg"
                        value={localBranding.clientBackgroundUrl || ''}
                        onChange={(e) => handleChange('clientBackgroundUrl', e.target.value)}
                        data-testid="bg-url-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Formats supportés: JPG, PNG, WebP
                      </p>
                    </div>
                  </div>
                )}

                {/* File Upload Mode */}
                {bgUploadMode === 'file' && (
                  <div className="space-y-4 p-4 rounded-lg border border-border">
                    <input
                      type="file"
                      ref={clientBgInputRef}
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Fichier trop volumineux', { description: 'Taille max: 5MB' });
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            handleChange('clientBackgroundFile', ev.target.result);
                            toast.success('Image importée');
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <div className="flex flex-col items-center gap-4 p-8 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors">
                      {localBranding.clientBackgroundFile ? (
                        <div className="text-center">
                          <div className="w-32 h-20 mx-auto mb-2 rounded overflow-hidden">
                            <img 
                              src={localBranding.clientBackgroundFile} 
                              alt="Fond" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button variant="outline" size="sm" onClick={() => clientBgInputRef.current?.click()}>
                              Remplacer
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleChange('clientBackgroundFile', null)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <ImagePlus className="w-12 h-12 text-muted-foreground" />
                          <div className="text-center">
                            <p className="font-medium">Glissez votre image ici</p>
                            <p className="text-sm text-muted-foreground">JPG, PNG, WebP - Max 5MB</p>
                          </div>
                          <Button onClick={() => clientBgInputRef.current?.click()}>
                            <Upload className="w-4 h-4 mr-2" />
                            Choisir un fichier
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Opacity Setting */}
                <div className="space-y-3">
                  <Label>Opacité de l'image ({Math.round((localBranding.clientBackgroundOpacity || 0.3) * 100)}%)</Label>
                  <Slider
                    value={[(localBranding.clientBackgroundOpacity || 0.3) * 100]}
                    onValueChange={([val]) => handleChange('clientBackgroundOpacity', val / 100)}
                    min={10}
                    max={100}
                    step={5}
                  />
                  <p className="text-xs text-muted-foreground">
                    Ajustez l'opacité pour que le texte reste lisible
                  </p>
                </div>

                {/* Preview */}
                {(localBranding.clientBackgroundUrl || localBranding.clientBackgroundFile) && (
                  <div className="space-y-2">
                    <Label>Aperçu</Label>
                    <div 
                      className="h-48 rounded-lg overflow-hidden relative"
                      style={{
                        backgroundImage: `url(${localBranding.clientBackgroundFile || localBranding.clientBackgroundUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <div 
                        className="absolute inset-0 bg-background"
                        style={{ opacity: 1 - (localBranding.clientBackgroundOpacity || 0.3) }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="p-4 rounded-lg bg-card/80 backdrop-blur">
                          <p className="font-medium">Exemple de carte</p>
                          <p className="text-sm text-muted-foreground">Le contenu reste lisible</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Gestion des Partenaires
                </CardTitle>
                <CardDescription>
                  Les logos des partenaires seront affichés sur le dashboard client sous le graphique d'évolution
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add New Partner */}
                <div className="p-4 border border-dashed border-border rounded-lg space-y-4">
                  <h4 className="font-medium">Ajouter un partenaire</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du partenaire *</Label>
                      <Input
                        value={newPartner.name}
                        onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ex: Binance, Coinbase..."
                        data-testid="partner-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL du logo</Label>
                      <Input
                        value={newPartner.logo_url}
                        onChange={(e) => setNewPartner(prev => ({ ...prev, logo_url: e.target.value, logo_base64: '' }))}
                        placeholder="https://exemple.com/logo.png"
                        data-testid="partner-logo-input"
                        disabled={!!newPartner.logo_base64}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ou importer un logo</Label>
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                setNewPartner(prev => ({ ...prev, logo_base64: ev.target.result, logo_url: '' }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="flex-1"
                          data-testid="partner-logo-file-input"
                        />
                        {newPartner.logo_base64 && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setNewPartner(prev => ({ ...prev, logo_base64: '' }))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {newPartner.logo_base64 && (
                        <div className="mt-2 p-2 bg-secondary/30 rounded flex justify-center">
                          <img src={newPartner.logo_base64} alt="Preview" className="h-12 object-contain" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Taille du logo</Label>
                      <Select 
                        value={newPartner.logo_size} 
                        onValueChange={(v) => setNewPartner(prev => ({ ...prev, logo_size: v }))}
                      >
                        <SelectTrigger data-testid="partner-size-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Petit (40px)</SelectItem>
                          <SelectItem value="medium">Moyen (64px)</SelectItem>
                          <SelectItem value="large">Grand (96px)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Site web (optionnel)</Label>
                      <Input
                        value={newPartner.website_url}
                        onChange={(e) => setNewPartner(prev => ({ ...prev, website_url: e.target.value }))}
                        placeholder="https://partenaire.com"
                        data-testid="partner-website-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ordre d'affichage</Label>
                      <Input
                        type="number"
                        value={newPartner.order}
                        onChange={(e) => setNewPartner(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        placeholder="0"
                        data-testid="partner-order-input"
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddPartner} data-testid="add-partner-btn">
                    Ajouter le partenaire
                  </Button>
                </div>

                {/* Partners List */}
                <div className="space-y-4">
                  <h4 className="font-medium">Partenaires actuels ({partners.length})</h4>
                  {partners.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Aucun partenaire configuré. Ajoutez votre premier partenaire ci-dessus.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {partners.map((partner) => (
                        <div key={partner.id} className="flex items-center gap-4 p-4 bg-secondary/30 rounded-lg">
                          <div className={`bg-white rounded-lg p-2 flex items-center justify-center flex-shrink-0 ${
                            partner.logo_size === 'small' ? 'w-12 h-12' : 
                            partner.logo_size === 'large' ? 'w-24 h-24' : 'w-16 h-16'
                          }`}>
                            <img 
                              src={partner.logo_base64 || partner.logo_url} 
                              alt={partner.name}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<span class="text-xs text-center text-muted-foreground">Logo invalide</span>`;
                              }}
                            />
                          </div>
                          
                          {editingPartner === partner.id ? (
                            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2">
                              <Input
                                defaultValue={partner.name}
                                id={`edit-name-${partner.id}`}
                                placeholder="Nom"
                              />
                              <Input
                                defaultValue={partner.logo_url || ''}
                                id={`edit-logo-${partner.id}`}
                                placeholder="URL du logo"
                              />
                              <Select defaultValue={partner.logo_size || 'medium'} onValueChange={(v) => { partner._editSize = v; }}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="small">Petit</SelectItem>
                                  <SelectItem value="medium">Moyen</SelectItem>
                                  <SelectItem value="large">Grand</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                defaultValue={partner.website_url || ''}
                                id={`edit-website-${partner.id}`}
                                placeholder="Site web"
                              />
                              <Input
                                type="number"
                                defaultValue={partner.order}
                                id={`edit-order-${partner.id}`}
                                placeholder="Ordre"
                              />
                            </div>
                          ) : (
                            <div className="flex-1">
                              <p className="font-medium">{partner.name}</p>
                              <p className="text-sm text-muted-foreground truncate">{partner.website_url || 'Pas de site web'}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-0.5 rounded ${partner.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                  {partner.is_active ? 'Actif' : 'Inactif'}
                                </span>
                                <span className="text-xs text-muted-foreground">Ordre: {partner.order}</span>
                                <span className="text-xs text-muted-foreground">Taille: {partner.logo_size || 'medium'}</span>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            {editingPartner === partner.id ? (
                              <>
                                <Button 
                                  size="sm" 
                                  onClick={() => {
                                    const name = document.getElementById(`edit-name-${partner.id}`).value;
                                    const logo_url = document.getElementById(`edit-logo-${partner.id}`).value;
                                    const website_url = document.getElementById(`edit-website-${partner.id}`).value;
                                    const order = parseInt(document.getElementById(`edit-order-${partner.id}`).value) || 0;
                                    const logo_size = partner._editSize || partner.logo_size || 'medium';
                                    handleUpdatePartner(partner.id, { name, logo_url, website_url, order, logo_size });
                                  }}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingPartner(null)}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="outline" onClick={() => setEditingPartner(partner.id)}>
                                  Modifier
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleUpdatePartner(partner.id, { is_active: !partner.is_active })}
                                >
                                  {partner.is_active ? 'Désactiver' : 'Activer'}
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDeletePartner(partner.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sharing Tab */}
          <TabsContent value="sharing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Apercu de partage
                </CardTitle>
                <CardDescription>
                  Personnalisez ce qui s'affiche quand quelqu'un partage le lien de votre plateforme sur WhatsApp, Telegram, etc.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Titre du site</Label>
                    <Input
                      value={localBranding.ogTitle || ''}
                      onChange={(e) => handleChange('ogTitle', e.target.value)}
                      placeholder={localBranding.platformName || 'Nom de votre plateforme'}
                      data-testid="og-title-input"
                    />
                    <p className="text-xs text-muted-foreground">Apparait en gras dans l'apercu du lien</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={localBranding.ogDescription || ''}
                      onChange={(e) => handleChange('ogDescription', e.target.value)}
                      placeholder="Description de votre plateforme"
                      data-testid="og-description-input"
                    />
                    <p className="text-xs text-muted-foreground">Apparait sous le titre dans l'apercu</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Image de partage</Label>
                  <div className="flex gap-4 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        value={localBranding.ogImage || ''}
                        onChange={(e) => handleChange('ogImage', e.target.value)}
                        placeholder="https://..."
                        data-testid="og-image-input"
                      />
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                        <Upload className="w-4 h-4" />
                        Uploader une image
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await axios.post(`${API}/upload-image`, formData, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                              handleChange('ogImage', fullUrl);
                              toast.success('Image uploadee');
                            } catch (err) {
                              toast.error('Erreur upload');
                            }
                          }}
                        />
                      </label>
                      <p className="text-xs text-muted-foreground">Taille recommandee : 1200x630 pixels</p>
                    </div>
                    {localBranding.ogImage && (
                      <img src={localBranding.ogImage} alt="OG Preview" className="w-32 h-20 rounded-lg object-cover border border-border/50" />
                    )}
                  </div>
                </div>

                {/* WhatsApp Preview */}
                <div className="mt-6 space-y-3">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wider">Apercu WhatsApp</Label>
                  <div className="p-4 rounded-xl bg-[#0b141a] max-w-md">
                    <div className="bg-[#1f2c34] rounded-lg overflow-hidden border border-[#2a3942]">
                      {localBranding.ogImage && (
                        <img src={localBranding.ogImage} alt="" className="w-full h-40 object-cover" />
                      )}
                      <div className="p-3 space-y-1">
                        <p className="font-semibold text-sm text-[#e9edef]">{localBranding.ogTitle || localBranding.platformName || 'Titre du site'}</p>
                        <p className="text-xs text-[#8696a0]">{localBranding.ogDescription || 'Description du site'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// Color Picker Component
function ColorPicker({ label, description, value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div className="relative">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-10 cursor-pointer rounded border border-border"
            data-testid={testId}
          />
        </div>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 font-mono text-sm uppercase"
          placeholder="#000000"
        />
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
}
